import axios from 'axios';

// Configuration de base d'axios
const API_URL = process.env.REACT_APP_API_URL || 'https://serv.ticket.rakopssolutions.com/api';

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Intercepteur pour ajouter le token à chaque requête
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs de réponse
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expiré ou invalide, déconnecter l'utilisateur
      console.warn('⚠️ [AUTH] Token expiré - Redirection vers login')
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// === AUTHENTIFICATION ===

export const authAPI = {
  login: (email, password) =>
    axiosInstance.post('/auth/login', { email, password }),

  register: (data) =>
    axiosInstance.post('/auth/register', data),

  logout: () =>
    axiosInstance.post('/auth/logout'),

  getMe: () =>
    axiosInstance.get('/auth/me'),

  updateProfile: (data) =>
    axiosInstance.put('/auth/profile', data),
};

// === CLIENTS ===

export const clientsAPI = {
  getAll: (params) =>
    axiosInstance.get('/clients', { params }),

  getById: (id) =>
    axiosInstance.get(`/clients/${id}`),

  create: (data) =>
    axiosInstance.post('/clients', data),

  update: (id, data) =>
    axiosInstance.put(`/clients/${id}`, data),

  delete: (id) =>
    axiosInstance.delete(`/clients/${id}`),
};

// === TEAMS ===

export const teamsAPI = {
  getAll: (params) =>
    axiosInstance.get('/teams', { params }),

  getById: (id) =>
    axiosInstance.get(`/teams/${id}`),

  create: (data) =>
    axiosInstance.post('/teams', data),

  update: (id, data) =>
    axiosInstance.put(`/teams/${id}`, data),

  delete: (id) =>
    axiosInstance.delete(`/teams/${id}`),

  toggleMember: (teamId, userId) =>
    axiosInstance.post(`/teams/${teamId}/toggle-member`, { user_id: userId }),
};

// === USERS / COLLABORATEURS ===

export const usersAPI = {
  getAll: (params) =>
    axiosInstance.get('/users', { params }),

  getById: (id) =>
    axiosInstance.get(`/users/${id}`),

  create: (data) =>
    axiosInstance.post('/users', data),

  update: (id, data) =>
    axiosInstance.put(`/users/${id}`, data),

  delete: (id) =>
    axiosInstance.delete(`/users/${id}`),

  getDocuments: (id) =>
    axiosInstance.get(`/users/${id}/documents`),

  uploadDocument: (id, formData) =>
    axiosInstance.post(`/users/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deleteDocument: (userId, docId) =>
    axiosInstance.delete(`/users/${userId}/documents/${docId}`),
};

// === PROJETS ===

export const projetsAPI = {
  getAll: (params) =>
    axiosInstance.get('/projets', { params }),

  getById: (id) =>
    axiosInstance.get(`/projets/${id}`),

  create: (data) =>
    axiosInstance.post('/projets', data),

  update: (id, data) =>
    axiosInstance.put(`/projets/${id}`, data),

  delete: (id) =>
    axiosInstance.delete(`/projets/${id}`),

  // Étapes
  getEtapes: (id) =>
    axiosInstance.get(`/projets/${id}/etapes`),

  addEtape: (id, data) =>
    axiosInstance.post(`/projets/${id}/etapes`, data),

  updateEtape: (projetId, etapeId, data) =>
    axiosInstance.put(`/projets/${projetId}/etapes/${etapeId}`, data),

  deleteEtape: (projetId, etapeId) =>
    axiosInstance.delete(`/projets/${projetId}/etapes/${etapeId}`),

  // Documents
  getDocuments: (id) =>
    axiosInstance.get(`/projets/${id}/documents`),

  uploadDocument: (id, formData) =>
    axiosInstance.post(`/projets/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deleteDocument: (projetId, docId) =>
    axiosInstance.delete(`/projets/${projetId}/documents/${docId}`),

  // Tickets
  getTickets: (id) =>
    axiosInstance.get(`/projets/${id}/tickets`),
};

// === TICKETS ===

export const ticketsAPI = {
  getAll: (params) =>
    axiosInstance.get('/tickets', { params }),

  getById: (id) =>
    axiosInstance.get(`/tickets/${id}`),

  create: (data) =>
    axiosInstance.post('/tickets', data),

  update: (id, data) =>
    axiosInstance.put(`/tickets/${id}`, data),

  delete: (id) =>
    axiosInstance.delete(`/tickets/${id}`),

  // Statut
  changeStatus: (id, statut) =>
    axiosInstance.put(`/tickets/${id}/statut`, { statut }),

  // Assignation
  assign: (id, userId) =>
    axiosInstance.put(`/tickets/${id}/assigner`, { user_id: userId }),

  // Commentaires
  getCommentaires: (id) =>
    axiosInstance.get(`/tickets/${id}/commentaires`),
  addCommentaire: (id, data) => {
    // Si c'est un FormData, l'envoyer tel quel avec le bon Content-Type
    // Sinon, c'est un simple string, l'envoyer comme JSON
    if (data instanceof FormData) {
      return axiosInstance.post(`/tickets/${id}/commentaires`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    }
    return axiosInstance.post(`/tickets/${id}/commentaires`, { commentaire: data })
  },

  // Pièces jointes
  getAttachments: (id) =>
    axiosInstance.get(`/tickets/${id}/attachments`),

  addAttachment: (id, formData) =>
    axiosInstance.post(`/tickets/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deleteAttachment: (ticketId, attachmentId) =>
    axiosInstance.delete(`/tickets/${ticketId}/attachments/${attachmentId}`),

  // Import
  import: (data) =>
    axiosInstance.post('/tickets/import', data),
};

// === MEETINGS ===

export const meetingsAPI = {
  getAll: (params) =>
    axiosInstance.get('/meetings', { params }),

  getById: (id) =>
    axiosInstance.get(`/meetings/${id}`),

  create: (data) =>
    axiosInstance.post('/meetings', data),

  update: (id, data) =>
    axiosInstance.put(`/meetings/${id}`, data),

  delete: (id) =>
    axiosInstance.delete(`/meetings/${id}`),

  // Participants
  addParticipants: (id, userIds) =>
    axiosInstance.post(`/meetings/${id}/participants`, { user_ids: userIds }),

  removeParticipant: (meetingId, userId) =>
    axiosInstance.delete(`/meetings/${meetingId}/participants/${userId}`),

  updatePresence: (meetingId, userId, statut) =>
    axiosInstance.put(`/meetings/${meetingId}/participants/${userId}/presence`, { statut_presence: statut }),

  // Compte rendu
  addCompteRendu: (id, compteRendu) =>
    axiosInstance.put(`/meetings/${id}/compte-rendu`, { compte_rendu: compteRendu }),
};

// === DASHBOARD ===

export const dashboardAPI = {
  getStats: () =>
    axiosInstance.get('/dashboard/stats'),

  getDisponibiliteEquipe: () =>
    axiosInstance.get('/dashboard/disponibilite-equipe'),

  getGantt: () =>
    axiosInstance.get('/dashboard/gantt'),

  getProjetsAvancement: () =>
    axiosInstance.get('/dashboard/projets-avancement'),

  getEcheances: () =>
    axiosInstance.get('/dashboard/echeances'),
};

// === NOTIFICATIONS ===

export const notificationsAPI = {
  getAll: (params) =>
    axiosInstance.get('/notifications', { params }),

  markAsRead: (id) =>
    axiosInstance.put(`/notifications/${id}/lire`),

  markAllAsRead: () =>
    axiosInstance.put('/notifications/tout-lire'),
};

// === POINTS SYSTEM ===

export const pointsAPI = {
  getHistory: () =>
    axiosInstance.get('/points/history'),

  getTeamHistory: () =>
    axiosInstance.get('/points/team-history'),

  getSettings: () =>
    axiosInstance.get('/points/settings'),

  updateSetting: (id, value) =>
    axiosInstance.put(`/points/settings/${id}`, { value }),
};

// === TICKET REQUESTS (Demandes de tickets) ===

export const ticketRequestsAPI = {
  getAll: () =>
    axiosInstance.get('/ticket-requests'),

  getById: (id) =>
    axiosInstance.get(`/ticket-requests/${id}`),

  create: (data) =>
    axiosInstance.post('/ticket-requests', data),

  approve: (id, data) =>
    axiosInstance.post(`/ticket-requests/${id}/approve`, data),

  reject: (id, data) =>
    axiosInstance.post(`/ticket-requests/${id}/reject`, data),

  getStats: () =>
    axiosInstance.get('/ticket-requests/stats/summary'),
};

// === WHATSAPP ===

export const whatsappAPI = {
  // On passe par le backend Laravel pour éviter les erreurs de "Mixed Content" (HTTP vs HTTPS)
  getStatus: () =>
    axiosInstance.get('/whatsapp/status'),
  logout: () =>
    axiosInstance.post('/whatsapp/logout'),
};

export default axiosInstance;
// === POINT RATES / GRILLE TARIFAIRE ===

export const pointRatesAPI = {
  getAll: () =>
    axiosInstance.get('/points/rates'),

  create: (data) =>
    axiosInstance.post('/points/rates', data),

  update: (id, data) =>
    axiosInstance.put(`/points/rates/${id}`, data),

  delete: (id) =>
    axiosInstance.delete(`/points/rates/${id}`),
};
